import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  parseISO,
} from "date-fns";
import {
  Card,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  List,
  LayoutGrid,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientContentCalendar = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/posts`);
      setPosts(response.data.posts || []);
    } catch (error) {
      toast.error("Failed to fetch posts");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: "status-draft",
      idea: "status-draft",
      pending_approval: "status-pending",
      approved: "status-approved",
      rejected: "status-rejected",
      change_requested: "status-change-requested",
      scheduled: "status-scheduled",
      posted: "status-posted",
    };
    return classes[status] || "status-draft";
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getPostsForDate = (date) => {
    return posts.filter((post) => {
      const dateStr = post.scheduled_date || post.created_at;
      if (!dateStr) return false;
      const postDate =
        typeof dateStr === "string" ? parseISO(dateStr) : dateStr;
      return isSameDay(postDate, date);
    });
  };

  const navigatePrev = () => {
    if (view === "month") {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const navigateNext = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const getCalendarDays = () => {
    if (view === "month") {
      const start = startOfWeek(startOfMonth(currentDate));
      const end = endOfWeek(endOfMonth(currentDate));
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return eachDayOfInterval({ start, end });
    }
  };

  const days = getCalendarDays();
  const today = new Date();

  return (
    <div className="space-y-3" data-testid="client-content-calendar">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
            Content Calendar
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            View your scheduled content
          </p>
        </div>

        {/* View Selector */}
        <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 p-1 w-fit">
          <Button
            variant={view === "month" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView("month")}
            data-testid="view-month"
          >
            <LayoutGrid className="w-4 h-4 mr-1" />
            Month
          </Button>
          <Button
            variant={view === "week" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView("week")}
            data-testid="view-week"
          >
            <CalendarDays className="w-4 h-4 mr-1" />
            Week
          </Button>
          <Button
            variant={view === "list" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView("list")}
            data-testid="view-list"
          >
            <List className="w-4 h-4 mr-1" />
            List
          </Button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={navigatePrev}
            data-testid="nav-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white min-w-[150px] text-center">
            {view === "month"
              ? format(currentDate, "MMMM yyyy")
              : `Week of ${format(startOfWeek(currentDate), "MMM d, yyyy")}`}
          </h2>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={navigateNext}
            data-testid="nav-next"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs h-7"
          onClick={() => setCurrentDate(new Date())}
        >
          Today
        </Button>
      </div>

      {/* Calendar / List View */}
      {loading ? (
        <div className="min-h-[280px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
        </div>
      ) : view === "list" ? (
        /* List View */
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-0">
            {posts.length === 0 ? (
              <div className="text-center py-16">
                <CalendarDays className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 dark:text-white mb-2">
                  No posts scheduled
                </h3>
                <p className="text-sm text-slate-500">
                  Your agency will add content here once it's ready for review
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    onClick={() => navigate(`/client/posts/${post.id}`)}
                    className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                    data-testid={`post-item-${post.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-900 dark:text-white line-clamp-2 mb-2">
                          {post.copy}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant="outline"
                            className={getStatusBadgeClass(post.status)}
                          >
                            {formatStatus(post.status)}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {post.post_type?.replace(/_/g, " ")}
                          </Badge>
                          {post.scheduled_date && (
                            <span className="text-sm text-slate-500">
                              {format(
                                typeof post.scheduled_date === "string"
                                  ? parseISO(post.scheduled_date)
                                  : post.scheduled_date,
                                "MMM d, yyyy h:mm a",
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      {post.image_urls?.[0] && (
                        <img
                          src={post.image_urls[0]}
                          alt=""
                          className="w-16 h-16 rounded-lg object-cover bg-slate-100"
                          referrerPolicy="no-referrer"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Calendar Grid View */
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-hidden">
          <CardContent className="p-0">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-slate-100 dark:border-slate-800">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="py-1.5 text-center text-xs font-medium text-slate-500"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div
              className={`grid grid-cols-7 ${view === "week" ? "" : "grid-rows-5"}`}
            >
              {days.map((day, idx) => {
                const dayPosts = getPostsForDate(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isToday = isSameDay(day, today);

                return (
                  <div
                    key={idx}
                    className={`min-h-[72px] p-1.5 border-b border-r border-slate-100 dark:border-slate-800 ${
                      !isCurrentMonth ? "bg-slate-50 dark:bg-slate-900/50" : ""
                    } ${isToday ? "bg-[#f0f4e8] dark:bg-[#556B2F]/10" : ""}`}
                  >
                    <div
                      className={`text-xs font-medium mb-1 ${
                        isToday
                          ? "text-[#556B2F]"
                          : isCurrentMonth
                            ? "text-slate-900 dark:text-white"
                            : "text-slate-400"
                      }`}
                    >
                      {format(day, "d")}
                    </div>

                    <div className="space-y-0.5">
                      {dayPosts.slice(0, 2).map((post) => (
                        <div
                          key={post.id}
                          onClick={() => navigate(`/client/posts/${post.id}`)}
                          className={`text-[10px] leading-tight px-1 py-0.5 rounded cursor-pointer truncate ${getStatusBadgeClass(post.status)} border`}
                          data-testid={`calendar-post-${post.id}`}
                        >
                          {post.copy?.substring(0, 22)}…
                        </div>
                      ))}
                      {dayPosts.length > 2 && (
                        <div className="text-[10px] text-slate-400 pl-0.5">
                          +{dayPosts.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientContentCalendar;
